// 免費遊戲倍率牌 — 對應 Unity GamePlayUI 的 _multiplierGroup / SetMultiplierText / PlayMainMultiplier。
//
// 視覺：優先用 Unity 原生 Spine `ani_multiplier`（吊牌木框＋繩，_multiplierInAnimation="in" 進場、
//   _multiplierChangeAnimation="change" 數值變更），倍率文字「X{n}」疊在骨骼上（對應 _multiplierText）。
//   倍率數字用 Unity 原生 tx_Multiplier 位圖字（LandscapeUI.prefab：TMP 空字型 Empty＋
//   spriteAsset tx_Multiplier、fontSize 70、格式 "X{0}"；吊牌 _multiplierText 與主倍率
//   _mainMultiplierText 皆同一套）。數值變更另播 `ani_multiplierAdd` 的 "multiplier" 爆點
//   （對應 _addMultiplierSpine）。骨骼不可用時退回自繪金色徽章。
//
// 行為：
//   - show()：進 FG 顯示，初始 X{FG_START_MULTIPLIER}，播 "in"。
//   - setMultiplier(n, animate)：更新文字；animate 時播 "change"＋爆點＋彈跳（se_multipler_add 由 AudioManager 播）。
//   - playMainMultiplier(n)：盤面倍率>1 的連鎖：大倍率字自盤面中央 scale 0→2.5（0.1s）→ 停 0.4s →
//     飛向倍率牌 0.35s（scale→1 再縮小淡出）。對照 Unity PlayMainMultiplier 的序列化參數。
//   - hide()：離開 FG 淡出。

import { Container, Graphics, Text } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import {
  unityAnchorLandscape,
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../../framework/render/design'
import { fontFamilyForLocale, t } from '../../../framework/i18n/i18n'
import { FG_START_MULTIPLIER } from '../../constants'
import { createEffectSpine, EFFECT_ANIM, hasAnimation } from '../../assets/EffectSpines'
import { BitmapNumber } from '../BitmapNumber'
import { killTweensOf, tweenTo } from '../tween'

/** 遞增彈跳動畫時長（秒）。 */
const PULSE_DURATION = 0.32
/** 主倍率飛入（Unity LandscapeUI.prefab 序列化值）：彈出 0.1s（scale 0→2.5）、停留 0.4s、位移 0.35s。 */
const MAIN_POP_DURATION = 0.1
const MAIN_POP_SCALE = 2.5
const MAIN_HOLD_DURATION = 0.4
const MAIN_FLY_DURATION = 0.35
/**
 * 主倍率飛行起/終點（Unity ScoreEffectGroup 的 StartDummy (0,150)／EndDummy (0,−340)，設計座標 y 向上）。
 * 終點即浮動計分（WinTemp）所在的盤面下緣，落定後在該處播 ani_score2 `win` 爆點（_scoreSpine 掛於 EndDummy）。
 */
const MAIN_START_AY = 150
const MAIN_END_AY = -340
/** 直版飛行起/終點（PoriraitUI 覆寫：StartDummy (0,222)／EndDummy (0,−199)）。 */
const MAIN_P_START_AY = 222
const MAIN_P_END_AY = -199
/** 直版倍率牌群組位置（PoriraitUI 覆寫 MultiplierInfo aPos (0, 687)＝頂部中央）。 */
const GROUP_P_AX = 0
const GROUP_P_AY = 687
/** 倍率數字設計字高（Unity 兩處 TMP 皆 fontSize 70，設計高 1080）。 */
const MULTIPLIER_FONT_DESIGN_SIZE = 70
/**
 * 倍率數字垂直微調（設計 px，正值＝往下；P1 使用者需求＝X{n} 要在木板正中央）。
 * 根因：tx_Multiplier 的數字與 X glyph 皆為「未裁切的 64px 格」，數字墨跡（坐落於基線之上）在格內偏上
 * 約 3.73 列＝設計 4.08px，而 BitmapNumber 以「格垂直中央」對齊 txt 骨骼；txt 骨骼本身又比 board mesh
 * 幾何中心再高 0.81px → 數字墨跡淨偏高約 4.9px（離線量測：scripts/measure-multiplier-txt.mjs
 * 解出 txt 骨骼 y=288.68／board mesh 中心 y=289.49；glyph alpha 分析得墨跡框中心偏上 3.73 列）。
 * 故把 value 在 valueAnchor 內下移 4.9 設計 px，使數字墨跡框中心落在木板幾何中心。
 * ⚠️ txt 槽 slot-object 矩陣（spine-pixi Spine.js:565-570）在 Skeleton.yDown=true 下 d=−bone.d=+1＝
 *    標準 pixi 方向（+y 向下），故此值為正；見 measure 腳本的矩陣驗算。此微調不需 ×s（valueAnchor
 *    已隨 plaqueSpine.scale=s 一起縮放），且僅適用於「骨骼綁定」路徑（字高恆為 70）。
 */
const MULTIPLIER_VALUE_CENTER_OFFSET = 4.9
/**
 * Unity LandscapeUI.prefab 的 MultiplierInfo 群組版面（1920×1080 設計座標，中心原點、y 向上）：
 * 群組 aPos (−822.64, 402.50)＝畫面左上角；骨骼原點相對群組 (1.41, +256.50)（吊繩自原點向上延伸出畫面頂）；
 * 倍率文字相對 (7.28, −32.18)；加倍爆點相對 (0, 0)。
 */
const GROUP_AX = -822.64
const GROUP_AY = 402.5
const SPINE_DX = 1.41
const SPINE_DY = 256.5
const TEXT_DX = 7.28
const TEXT_DY = -32.18

export class MultiplierView extends Container implements Resizable {
  /** Unity 原生倍率吊牌骨骼（載入失敗為 null → 顯示自繪徽章）。 */
  private readonly plaqueSpine: Spine | null
  /** 數值變更爆點（ani_multiplierAdd "multiplier"）。 */
  private readonly addBurstSpine: Spine | null
  private readonly badge = new Graphics()
  private readonly caption = new Text({
    text: t('multiplier.caption'),
    style: { fontFamily: fontFamilyForLocale(), fontSize: 16, fill: 0xfff0c0, fontWeight: 'bold', stroke: { color: 0x6a3a06, width: 3 } },
  })
  /**
   * 吊牌倍率數字（tx_Multiplier 位圖字，對應 _multiplierText 的 "X{n}"）。
   * 骨骼可用時以 addSlotObject 綁到 ani_multiplier 的 `txt` 槽，跟隨面板 "in"/"change" 動態移動
   * （對應 Unity _multiplierText 掛的 BoneFollowerGraphic）；見建構子與 valueFollowsBone。
   */
  private readonly value = new BitmapNumber('multiplier')
  /**
   * value 綁骨骼 `txt` 槽用的中介容器（pivot=0）。⚠️ spine-pixi 的 addSlotObject 每幀把物件 position 設為
   * `boneWorld + object.pivot`（把 pivot 加回去），會抵銷 BitmapNumber 的水平置中 pivot(x/2)，使 X3 中心
   * 右移半個字寬。故綁一個 pivot=0 的 wrapper、value 在其內以自身 pivot 置中於原點 → X3 中心落在 txt 骨骼
   * （與 Unity _multiplierText 的 BoneFollowerGraphic 對齊一致）。見建構子 valueFollowsBone 分支。
   */
  private readonly valueAnchor = new Container()
  /** 主倍率飛入用的大字（獨立於徽章，飛行時才顯示；同 tx_Multiplier，對應 _mainMultiplierText）。 */
  private readonly flyText = new BitmapNumber('multiplier')
  /** 主倍率落定爆點（ani_score2 `win`，對應 _scoreSpine 掛於 EndDummy；載入失敗為 null 則略過）。 */
  private readonly scoreBurstSpine: Spine | null

  private screenW = 0
  private screenH = 0
  private orientation: Orientation = 'landscape'
  /** 徽章內容（骨骼/自繪＋文字）的縮放對象，用於遞增彈跳，不影響整體 alpha 淡入。 */
  private readonly badgeGroup = new Container()
  /** value 是否已 addSlotObject 綁到骨骼 `txt` 槽（true＝位置/縮放由骨骼驅動，不手動定位）。 */
  private valueFollowsBone = false

  constructor() {
    super()
    this.visible = false

    this.caption.anchor.set(0.5)
    // value/flyText 為 BitmapNumber，自身以 pivot 置中（等同 anchor 0.5）。
    this.flyText.visible = false

    this.plaqueSpine = createEffectSpine('multiplier')
    this.addBurstSpine = createEffectSpine('multiplierAdd')
    this.scoreBurstSpine = createEffectSpine('score2')
    if (this.scoreBurstSpine !== null) {
      this.scoreBurstSpine.visible = false
      this.addChild(this.scoreBurstSpine)
    }

    if (this.plaqueSpine !== null) {
      this.badge.visible = false
      this.caption.visible = false
      this.badgeGroup.addChild(this.plaqueSpine)
    } else {
      this.badgeGroup.addChild(this.badge, this.caption)
    }
    // 倍率文字跟隨骨骼（對應 Unity _multiplierText 掛的 BoneFollowerGraphic）：spine-pixi 的 addSlotObject
    // 會把物件重新掛到 plaqueSpine 底下，並每幀以該 `txt` 槽骨骼的世界矩陣覆寫其位置/旋轉/縮放，
    // 故面板播 "in"/"change" 時文字會一起動（使用者需求）。`txt` 槽無圖集區域，靠預設
    // followAttachmentTimeline=false 維持可見。骨骼不存在或無 `txt` 槽時退回舊的靜態疊字。
    // ⚠️ 綁的是 valueAnchor（pivot=0）而非 value 本身：addSlotObject 每幀把物件 position 設為
    //    `boneWorld + object.pivot`，若直接綁 value 會把它水平置中的 pivot(x/2) 加回去、抵銷置中而使
    //    X3 右移半個字寬。改讓 value 在 pivot=0 的 wrapper 內以自身 pivot 置中於原點 → X3 中心落在 txt 骨骼。
    const txtSlot = this.plaqueSpine?.skeleton.findSlot('txt') ?? null
    if (this.plaqueSpine !== null && txtSlot !== null) {
      this.valueAnchor.addChild(this.value)
      this.plaqueSpine.addSlotObject('txt', this.valueAnchor)
      // P1：數字往下微調，使墨跡框中心落在木板正中央（見 MULTIPLIER_VALUE_CENTER_OFFSET）。
      this.value.position.set(0, MULTIPLIER_VALUE_CENTER_OFFSET)
      this.valueFollowsBone = true
    } else {
      this.badgeGroup.addChild(this.value)
    }
    if (this.addBurstSpine !== null) {
      this.addBurstSpine.visible = false
      this.badgeGroup.addChild(this.addBurstSpine)
    }
    this.addChild(this.badgeGroup, this.flyText)
  }

  /** 顯示倍率牌並重置為起始倍率（進入 FG 時由 GameController 呼叫）；骨骼播 "in" 進場。 */
  show(): void {
    this.setMultiplier(FG_START_MULTIPLIER, false)
    this.visible = true
    killTweensOf(this)
    killTweensOf(this.badgeGroup.scale)
    this.alpha = 0
    this.badgeGroup.scale.set(0.5)
    void tweenTo(this, { alpha: 1, duration: 0.3 })
    void tweenTo(this.badgeGroup.scale, { x: 1, y: 1, duration: 0.42, ease: 'back.out(2)' })
    if (this.plaqueSpine !== null && hasAnimation(this.plaqueSpine, EFFECT_ANIM.multiplierIn)) {
      this.plaqueSpine.state.setAnimation(0, EFFECT_ANIM.multiplierIn, false)
    }
  }

  /** 淡出隱藏倍率牌（離開 FG 時由 GameController 呼叫）。 */
  hide(): void {
    killTweensOf(this)
    killTweensOf(this.badgeGroup.scale)
    killTweensOf(this.flyText)
    killTweensOf(this.flyText.scale)
    this.flyText.visible = false
    void tweenTo(this, {
      alpha: 0,
      duration: 0.3,
    }).then(() => {
      if (!this.destroyed) this.visible = false
    })
  }

  /** 更新倍率數字（"X{n}"，對應 Unity SetMultiplierText 格式）；animate 時骨骼播 "change"＋爆點＋牌子彈跳。 */
  setMultiplier(multiplier: number, animate: boolean): void {
    this.value.setText(`X${multiplier}`)
    if (!animate) return
    killTweensOf(this.badgeGroup.scale)
    this.badgeGroup.scale.set(1.25)
    void tweenTo(this.badgeGroup.scale, { x: 1, y: 1, duration: PULSE_DURATION, ease: 'back.out(3)' })
    if (this.plaqueSpine !== null && hasAnimation(this.plaqueSpine, EFFECT_ANIM.multiplierChange)) {
      this.plaqueSpine.state.setAnimation(0, EFFECT_ANIM.multiplierChange, false)
    }
    if (this.addBurstSpine !== null && hasAnimation(this.addBurstSpine, EFFECT_ANIM.multiplierAddBurst)) {
      this.addBurstSpine.visible = true
      const entry = this.addBurstSpine.state.setAnimation(0, EFFECT_ANIM.multiplierAddBurst, false)
      const burst = this.addBurstSpine
      const ms = Math.max(0.3, entry.animation?.duration ?? 0.6) * 1000
      setTimeout(() => {
        if (!burst.destroyed) burst.visible = false
      }, ms)
    }
  }

  /** 倍率牌中心的螢幕座標（獎勵子彈飛行終點）。 */
  plaqueCenter(): { x: number; y: number } {
    return this.badgeCenter()
  }

  /**
   * 主倍率飛入：大倍率字於 StartDummy (0,150) 縮放彈出、停留，再飛向 EndDummy (0,−340)（＝浮動計分
   * WinTemp 所在）縮小消失，落定後於該處播 ani_score2 `win` 爆點（非阻塞，對應 Unity entryTrack.Complete 隱藏）。
   * 對應 Unity GamePlayUI.PlayMainMultiplier；由 GameController 於「盤面倍率>1 的連鎖」時 await。
   */
  async playMainMultiplier(multiplier: number): Promise<void> {
    if (this.screenW === 0) return
    const start = this.mainFlightPoint(MAIN_START_AY)
    const end = this.mainFlightPoint(MAIN_END_AY)

    this.flyText.setText(`X${multiplier}`)
    this.flyText.setHeight(this.mainFontSize())
    this.flyText.position.set(start.x, start.y)
    this.flyText.scale.set(0)
    this.flyText.alpha = 1
    this.flyText.visible = true

    killTweensOf(this.flyText)
    killTweensOf(this.flyText.scale)
    // ① StartDummy 縮放彈出（0→2.5，0.1s）
    await tweenTo(this.flyText.scale, { x: MAIN_POP_SCALE, y: MAIN_POP_SCALE, duration: MAIN_POP_DURATION, ease: 'back.out(1.8)' })
    // ② 停留 0.4s
    await tweenTo(this.flyText, { alpha: 1, duration: MAIN_HOLD_DURATION })
    // ③ 飛向 EndDummy 0.35s，scale 2.5→1 並淡出
    await Promise.all([
      tweenTo(this.flyText, { x: end.x, y: end.y, alpha: 0.25, duration: MAIN_FLY_DURATION, ease: 'power2.in' }),
      tweenTo(this.flyText.scale, { x: 1, y: 1, duration: MAIN_FLY_DURATION, ease: 'power2.in' }),
    ])
    this.flyText.visible = false

    // 落定爆點（fire-and-forget；Unity: _scoreSpine SetActive→播 win→Complete 後隱藏）。
    const burst = this.scoreBurstSpine
    if (burst !== null && hasAnimation(burst, EFFECT_ANIM.scoreWin)) {
      burst.position.set(end.x, end.y)
      burst.scale.set(unityScaleLandscape(this.screenW, this.screenH))
      burst.visible = true
      const entry = burst.state.setAnimation(0, EFFECT_ANIM.scoreWin, false)
      const ms = Math.max(0.3, entry.animation?.duration ?? 0.6) * 1000
      setTimeout(() => {
        if (!burst.destroyed) burst.visible = false
      }, ms)
    }
  }

  /**
   * 主倍率飛行點。橫版 StartDummy (0,150)／EndDummy (0,−340)；
   * 直版照 PoriraitUI.prefab 覆寫：StartDummy (0,222)／EndDummy (0,−199)。
   */
  private mainFlightPoint(ay: number): { x: number; y: number } {
    if (this.orientation === 'portrait') {
      const pay = ay === MAIN_START_AY ? MAIN_P_START_AY : MAIN_P_END_AY
      return unityAnchorPortrait(0, pay, this.screenW, this.screenH)
    }
    return unityAnchorLandscape(0, ay, this.screenW, this.screenH)
  }

  private badgeCenter(): { x: number; y: number } {
    // 橫版照 Unity MultiplierInfo aPos (−822.64, 402.50)＝畫面左上角，吊繩向上延伸至畫面頂；
    // 直版照 PoriraitUI 覆寫 aPos (0, 687)＝頂部中央。
    if (this.orientation === 'portrait') {
      return unityAnchorPortrait(GROUP_P_AX, GROUP_P_AY, this.screenW, this.screenH)
    }
    return unityAnchorLandscape(GROUP_AX, GROUP_AY, this.screenW, this.screenH)
  }

  private mainFontSize(): number {
    return Math.max(48, Math.min(this.screenW, this.screenH) * 0.16)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.screenW = width
    this.screenH = height
    this.orientation = orientation

    // resize 中斷進行中的主倍率飛入，避免飛向舊版面座標（動畫下次觸發時自然恢復）。
    killTweensOf(this.flyText)
    killTweensOf(this.flyText.scale)
    this.flyText.visible = false

    const center = this.badgeCenter()
    // 橫/直版皆以 Unity 等比縮放係數排版（骨骼/文字用設計座標原值；直版 PoriraitUI 未覆寫群組 scale）。
    const s = orientation === 'portrait' ? unityScalePortrait(width, height) : unityScaleLandscape(width, height)
    const badgeW = 238 * s
    const badgeH = badgeW * 0.62

    this.badgeGroup.position.set(center.x, center.y)

    if (this.plaqueSpine !== null) {
      // 骨骼原點照 Unity Multiplier 節點：群組中心 + (1.41, +256.50)（y 向上），吊繩向上出畫面頂。
      this.plaqueSpine.scale.set(s)
      this.plaqueSpine.position.set(SPINE_DX * s, -SPINE_DY * s)
      if (this.addBurstSpine !== null) {
        this.addBurstSpine.scale.set(s)
        this.addBurstSpine.position.set(0, 0)
      }
    } else {
      this.drawBadge(badgeW, badgeH)
      this.caption.style.fontSize = Math.max(11, badgeH * 0.22)
      this.caption.position.set(0, -badgeH * 0.3)
    }
    // 倍率數字：Unity MultiplierText fontSize 70。
    if (this.valueFollowsBone) {
      // 已 addSlotObject 綁到骨骼 `txt` 槽：位置/旋轉/縮放由骨骼每幀驅動，這裡只設「設計字高」，
      // 螢幕縮放由 plaqueSpine.scale = s 提供（故不 × s）。垂直置中微調在建構子一次性設定
      // （value.position.y = MULTIPLIER_VALUE_CENTER_OFFSET，不隨 resize 變動）。
      this.value.setHeight(MULTIPLIER_FONT_DESIGN_SIZE)
    } else {
      // 靜態疊字（無骨骼/無 `txt` 槽）：相對群組 (7.28, −32.18)。
      this.value.setHeight(Math.max(22, MULTIPLIER_FONT_DESIGN_SIZE * s))
      this.value.position.set(
        this.plaqueSpine !== null ? TEXT_DX * s : 0,
        this.plaqueSpine !== null ? -TEXT_DY * s : badgeH * 0.12,
      )
    }
  }

  /** 金色圓角徽章（金邊 + 深色內底 + 上緣高光），以 badgeGroup 本地原點為中心。fallback 用。 */
  private drawBadge(w: number, h: number): void {
    const r = h * 0.28
    this.badge
      .clear()
      .roundRect(-w / 2 - 4, -h / 2 - 4, w + 8, h + 8, r + 4)
      .fill({ color: 0xffcf5a, alpha: 0.95 })
      .roundRect(-w / 2, -h / 2, w, h, r)
      .fill({ color: 0x3a1c46, alpha: 0.98 })
      .roundRect(-w / 2, -h / 2, w, h, r)
      .stroke({ color: 0xffe9a6, width: Math.max(2, h * 0.05), alpha: 0.95 })
      .roundRect(-w / 2 + w * 0.08, -h / 2 + h * 0.1, w * 0.84, h * 0.22, r * 0.5)
      .fill({ color: 0xffffff, alpha: 0.12 })
  }
}
